'use client';

import Icon from '@/components/ui/AppIcon';
import DemoDataPill from './DemoDataPill';
import type {
  QBReportResponse,
  QBCashFlowReport,
  QBBalanceSheetReport,
  IntuitReportResponse,
  IntuitReportRow,
} from '@/lib/quickbooks/types';

interface ReportState<T> {
  data: QBReportResponse<T> | null;
  loading: boolean;
  error: string | null;
}

interface Props {
  cashFlow:     ReportState<QBCashFlowReport>;
  balanceSheet: ReportState<QBBalanceSheetReport>;
  onRetryCash:  () => void;
  onRetryBS:    () => void;
}

// ── Parse helpers ──────────────────────────────────────────────────────────

interface SectionSummary {
  label: string;
  value: number;
  rows: Array<{ label: string; value: number }>;
}

function parseSections(report: IntuitReportResponse | undefined): SectionSummary[] {
  if (!report?.Rows?.Row) return [];
  const out: SectionSummary[] = [];
  for (const top of report.Rows.Row) {
    const headerLabel = top.Header?.ColData?.[0]?.value ?? top.Summary?.ColData?.[0]?.value;
    const summaryVal  = top.Summary?.ColData?.[1]?.value;
    if (!headerLabel || summaryVal === undefined) continue;
    const subRows: Array<{ label: string; value: number }> = [];
    if (top.Rows?.Row) {
      for (const inner of top.Rows.Row) {
        if (inner.type === 'Data' && inner.ColData) {
          const lbl = inner.ColData[0]?.value ?? '';
          const v   = parseFloat(inner.ColData[1]?.value ?? '0') || 0;
          if (lbl) subRows.push({ label: lbl, value: v });
        }
      }
    }
    out.push({ label: headerLabel, value: parseFloat(summaryVal) || 0, rows: subRows });
  }
  return out;
}

function formatMoney(val: number): string {
  const abs = Math.abs(val);
  const sign = val < 0 ? '-' : '';
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000)     return `${sign}$${(abs / 1_000).toFixed(1)}k`;
  return `${sign}$${abs.toFixed(0)}`;
}

function staleLabel(fetchedAt: string): string {
  const mins = Math.floor((Date.now() - new Date(fetchedAt).getTime()) / 60_000);
  if (mins < 60) return `${mins}m`;
  return `${Math.floor(mins / 60)}h`;
}

// ── Component ─────────────────────────────────────────────────────────────

export default function Row5CashFlowBalanceSheet({ cashFlow, balanceSheet, onRetryCash, onRetryBS }: Props) {
  return (
    <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <CashFlowCard cashFlow={cashFlow} onRetry={onRetryCash} />
      <BalanceSheetCard balanceSheet={balanceSheet} onRetry={onRetryBS} />
    </section>
  );
}

// ── 5A. Cash Flow · Operating / Investing / Financing ────────────────────

function CashFlowCard({ cashFlow, onRetry }: { cashFlow: ReportState<QBCashFlowReport>; onRetry: () => void }) {
  const isLoading = cashFlow.loading && !cashFlow.data;
  const isError = !cashFlow.loading && cashFlow.error && !cashFlow.data;
  const sections = parseSections(cashFlow.data?.data);
  const mock = !!cashFlow.data?.mock;
  const stale = !!cashFlow.data?.staleOnError;

  // Find Operating / Investing / Financing sections; ignore the trailing "Net Cash Change" if present.
  const opening = sections.find((s) => s.label.toLowerCase().includes('operating'));
  const investing = sections.find((s) => s.label.toLowerCase().includes('investing'));
  const financing = sections.find((s) => s.label.toLowerCase().includes('financing'));
  const netChange = (opening?.value ?? 0) + (investing?.value ?? 0) + (financing?.value ?? 0);

  const items = [
    { section: opening,   tone: '#10B981', icon: 'ArrowTrendingUpIcon'   },
    { section: investing, tone: '#F59E0B', icon: 'BuildingOffice2Icon' },
    { section: financing, tone: '#8B5CF6', icon: 'BanknotesIcon'        },
  ].filter((it) => !!it.section);

  return (
    <div className="relative rounded-xl p-5" style={{ background: '#FFFFFF', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid #F0F0F0' }}>
      {mock && <DemoDataPill />}
      <div className="flex items-center justify-between gap-2 mb-1 pr-16">
        <div className="flex items-center gap-2">
          <Icon name="ArrowsUpDownIcon" size={15} style={{ color: '#033494' }} />
          <h3 className="text-base font-bold" style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}>
            Cash Flow · Operating / Investing / Financing
          </h3>
        </div>
      </div>
      <p className="text-xs text-gray-400 mb-3">
        Net cash change:{' '}
        <span className="font-bold" style={{ color: netChange >= 0 ? '#10B981' : '#EF4444' }}>
          {formatMoney(netChange)}
        </span>
      </p>
      {stale && cashFlow.data && (
        <p className="text-[10px] italic text-gray-400 mb-2">
          Data may be stale · refreshed {staleLabel(cashFlow.data.fetchedAt)} ago
        </p>
      )}

      {isLoading ? (
        <LoadingBody />
      ) : isError ? (
        <ErrorBody error={cashFlow.error!} onRetry={onRetry} tag="[CashFlow]" />
      ) : items.length === 0 ? (
        <EmptyBody message="No cash flow data for the selected period" />
      ) : (
        <div className="space-y-3">
          {items.map((it, i) => {
            const s = it.section!;
            const positive = s.value >= 0;
            return (
              <div key={i} className="rounded-lg p-3" style={{ border: '1px solid #F3F4F6', background: 'rgba(248,250,252,0.5)' }}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-1.5">
                    <Icon name={it.icon} size={13} style={{ color: it.tone }} />
                    <p className="text-[11px] uppercase tracking-wider font-semibold text-gray-500">{s.label}</p>
                  </div>
                  <p className="text-sm font-bold font-mono" style={{ color: positive ? '#047857' : '#B91C1C' }}>
                    {formatMoney(s.value)}
                  </p>
                </div>
                {s.rows.length > 0 && (
                  <div className="space-y-0.5">
                    {s.rows.slice(0, 5).map((r, ri) => (
                      <div key={ri} className="flex items-center justify-between text-[11px]">
                        <span className="text-gray-600 truncate">{r.label}</span>
                        <span className="font-mono text-gray-700">{formatMoney(r.value)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── 5B. Balance Sheet Summary ─────────────────────────────────────────────

function BalanceSheetCard({ balanceSheet, onRetry }: { balanceSheet: ReportState<QBBalanceSheetReport>; onRetry: () => void }) {
  const isLoading = balanceSheet.loading && !balanceSheet.data;
  const isError = !balanceSheet.loading && balanceSheet.error && !balanceSheet.data;
  const sections = parseSections(balanceSheet.data?.data);
  const mock = !!balanceSheet.data?.mock;
  const stale = !!balanceSheet.data?.staleOnError;

  const assets      = sections.find((s) => s.label.toLowerCase().includes('asset'));
  const liabilities = sections.find((s) => s.label.toLowerCase().includes('liab'));
  const equity      = sections.find((s) => s.label.toLowerCase().includes('equity'));

  // Working capital = Current Assets - Current Liabilities (approximated as
  // total assets - total liabilities for a high-level executive summary;
  // QB's detail-level current-vs-fixed split isn't always present in mock).
  const workingCapital = (assets?.value ?? 0) - (liabilities?.value ?? 0);

  // For the relative-size bars, scale to the largest section.
  const maxVal = Math.max(assets?.value ?? 0, liabilities?.value ?? 0, equity?.value ?? 0, 1);

  const sectionList = [
    { section: assets,      tone: '#10B981' },
    { section: liabilities, tone: '#F59E0B' },
    { section: equity,      tone: '#033494' },
  ].filter((s) => !!s.section);

  return (
    <div className="relative rounded-xl p-5" style={{ background: '#FFFFFF', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid #F0F0F0' }}>
      {mock && <DemoDataPill />}
      <div className="flex items-center justify-between gap-2 mb-3 pr-16">
        <div className="flex items-center gap-2">
          <Icon name="BookOpenIcon" size={15} style={{ color: '#033494' }} />
          <h3 className="text-base font-bold" style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}>
            Balance Sheet Summary
          </h3>
        </div>
      </div>
      {stale && balanceSheet.data && (
        <p className="text-[10px] italic text-gray-400 mb-2">
          Data may be stale · refreshed {staleLabel(balanceSheet.data.fetchedAt)} ago
        </p>
      )}

      {isLoading ? (
        <LoadingBody />
      ) : isError ? (
        <ErrorBody error={balanceSheet.error!} onRetry={onRetry} tag="[BalanceSheet]" />
      ) : sectionList.length === 0 ? (
        <EmptyBody message="Balance sheet data unavailable" />
      ) : (
        <>
          <div className="space-y-2">
            {sectionList.map((it, i) => {
              const s = it.section!;
              const pct = (s.value / maxVal) * 100;
              return (
                <div key={i}>
                  <div className="flex items-center justify-between mb-0.5">
                    <p className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: it.tone }}>
                      {s.label}
                    </p>
                    <p className="text-sm font-bold font-mono" style={{ color: '#000150' }}>
                      {formatMoney(s.value)}
                    </p>
                  </div>
                  <div className="h-2 rounded-full" style={{ background: '#F3F4F6' }}>
                    <div className="h-2 rounded-full" style={{ width: `${pct}%`, background: it.tone }} />
                  </div>
                  {s.rows.length > 0 && (
                    <div className="mt-1 ml-0.5 space-y-0.5">
                      {s.rows.slice(0, 4).map((r, ri) => (
                        <div key={ri} className="flex items-center justify-between text-[10px]">
                          <span className="text-gray-600 truncate">{r.label}</span>
                          <span className="font-mono text-gray-500">{formatMoney(r.value)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="mt-3 pt-3 border-t border-gray-100 grid grid-cols-2 gap-2 text-[11px]">
            <div>
              <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-500">Total Equity</p>
              <p className="text-sm font-bold" style={{ color: '#000150' }}>{formatMoney(equity?.value ?? 0)}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-500">Working Capital</p>
              <p className="text-sm font-bold" style={{ color: workingCapital >= 0 ? '#047857' : '#B91C1C' }}>
                {formatMoney(workingCapital)}
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── Shared sub-views ──────────────────────────────────────────────────────

function LoadingBody() {
  return (
    <div className="py-8 flex items-center justify-center">
      <div className="w-5 h-5 rounded-full border-[2.5px] border-[#07B0E9] border-t-transparent animate-spin" />
    </div>
  );
}

function ErrorBody({ error, onRetry, tag }: { error: string; onRetry: () => void; tag: string }) {
  return (
    <div className="rounded-lg p-3 flex items-start gap-2" style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.18)' }}>
      <Icon name="ExclamationTriangleIcon" size={16} style={{ color: '#B91C1C', flexShrink: 0 }} />
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold" style={{ color: '#7F1D1D' }}>{tag} fetch failed</p>
        <p className="text-[11px] text-gray-600 mt-0.5 break-words">{error}</p>
        <button
          onClick={onRetry}
          className="mt-2 rounded-md px-2.5 py-1 text-[11px] font-semibold text-white hover:opacity-90 active:scale-95 transition"
          style={{ background: '#EF4444' }}
        >
          Retry
        </button>
      </div>
    </div>
  );
}

function EmptyBody({ message }: { message: string }) {
  return (
    <div className="rounded-lg p-3 flex items-center gap-2" style={{ background: 'rgba(107,114,128,0.06)', border: '1px solid rgba(107,114,128,0.18)' }}>
      <Icon name="DocumentTextIcon" size={16} style={{ color: '#6B7280' }} />
      <p className="text-xs font-medium text-gray-600">{message}</p>
    </div>
  );
}
